<template>
    <div class="facility-settings-tab tab-content">
        <div class="table-container-compact">
            <table class="table-compact">
                <thead>
                    <tr>
                        <th class="text-center">시설 코드</th>
                        <th class="text-center">시설명</th>
                        <th class="text-center">시설 유형</th>
                        <th class="text-center">조직</th>
                        <th class="text-center">주소</th>
                        <th class="text-center">담당자</th>
                        <th class="text-center">상태</th>
                        <th class="text-center"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="loading">
                        <td colspan="8" class="text-center"><i class="fas fa-spinner fa-spin"></i> 로딩 중</td>
                    </tr>
                    <tr v-else-if="paginatedFacilities.length === 0">
                        <td colspan="8" class="text-center empty-state">
                            <i class="fas fa-building"></i>
                            <p>등록된 시설이 없습니다</p>
                        </td>
                    </tr>
                    <tr
                        v-else
                        v-for="facility in paginatedFacilities"
                        :key="facility.facilityCode"
                        :class="{ 'inactive-row': facility.status === 'INACTIVE' }"
                        class="clickable-row"
                        @click="viewFacilityDetail(facility)"
                    >
                        <td class="text-center">
                            <strong class="facility-code-cell">{{ facility.facilityCode }}</strong>
                        </td>
                        <td class="text-center">{{ facility.facilityName || "-" }}</td>
                        <td class="text-center">{{ formatFacilityType(facility.facilityType) }}</td>
                        <td class="text-center">{{ getOrgLabel(facility.orgIdx) }}</td>
                        <td class="text-center">{{ facility.address || "-" }}</td>
                        <td class="text-center">
                            <template v-if="facility.facilityIdx">
                                <span v-if="(facility.mappedUserCount || 0) > 0" class="assignee-count">{{
                                    facility.mappedUserCount
                                }}</span>
                                <span v-else class="text-muted">0</span>
                            </template>
                            <span v-else class="text-muted">-</span>
                        </td>
                        <td class="text-center">{{ formatStatus(facility.status) }}</td>
                        <td @click.stop>
                            <div class="action-buttons text-center">
                                <IconButton
                                    v-if="facility.facilityIdx"
                                    icon="fas fa-users"
                                    title="담당자"
                                    @click.stop="openAssigneesModal(facility)"
                                />
                                <IconButton
                                    v-if="facility.facilityIdx"
                                    icon="fas fa-paperclip"
                                    title="증빙"
                                    @click.stop="openFacilityEvidenceModal(facility)"
                                />
                                <IconButton
                                    icon="fas fa-edit"
                                    title="수정"
                                    @click.stop="openFacilityModal('edit', facility)"
                                />
                                <IconButton
                                    icon="fas fa-trash"
                                    variant="delete"
                                    title="삭제"
                                    @click.stop="confirmDeleteFacility(facility)"
                                />
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <TablePagination
            v-if="pagination.total > 0"
            :total-items="pagination.total"
            :current-page="pagination.page"
            :page-size="pagination.limit"
            @update:currentPage="onFacilityPageChange"
            @update:pageSize="onFacilityPageSizeChange"
        />

        <!-- 시설 상세 모달 -->
        <b-modal v-model="showDetailModal" title="시설 상세 정보" size="md" centered>
            <template #modal-header="{ close }">
                <div class="modal-detail-header">
                    <h5 class="modal-title">시설 상세 정보</h5>
                    <button type="button" class="close" @click="close">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
            </template>
            <div v-if="selectedFacility" class="modal-detail-content-compact">
                <table class="detail-table-compact">
                    <tbody>
                        <tr>
                            <td class="detail-label">시설 코드</td>
                            <td class="detail-value">{{ selectedFacility.facilityCode }}</td>
                        </tr>
                        <tr>
                            <td class="detail-label">시설명</td>
                            <td class="detail-value">{{ selectedFacility.facilityName }}</td>
                        </tr>
                        <tr>
                            <td class="detail-label">조직</td>
                            <td class="detail-value">{{ getOrgLabel(selectedFacility.orgIdx) }}</td>
                        </tr>
                        <tr>
                            <td class="detail-label">시설 유형</td>
                            <td class="detail-value">{{ formatFacilityType(selectedFacility.facilityType) }}</td>
                        </tr>
                        <tr>
                            <td class="detail-label">주소</td>
                            <td class="detail-value">{{ selectedFacility.address || "-" }}</td>
                        </tr>
                        <tr>
                            <td class="detail-label">국가</td>
                            <td class="detail-value">{{ selectedFacility.country || "-" }}</td>
                        </tr>
                        <tr>
                            <td class="detail-label">지역</td>
                            <td class="detail-value">{{ selectedFacility.region || "-" }}</td>
                        </tr>
                        <tr>
                            <td class="detail-label">법인</td>
                            <td class="detail-value">{{ selectedFacility.legalEntity || "-" }}</td>
                        </tr>
                        <tr>
                            <td class="detail-label">상태</td>
                            <td class="detail-value">{{ formatStatus(selectedFacility.status) }}</td>
                        </tr>
                        <tr v-if="selectedFacility.remarks">
                            <td class="detail-label">비고</td>
                            <td class="detail-value">{{ selectedFacility.remarks }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <template #modal-footer>
                <CarbonButton variant="secondary" @click="showDetailModal = false">닫기</CarbonButton>
                <CarbonButton
                    variant="primary"
                    @click="
                        openFacilityModal('edit', selectedFacility);
                        showDetailModal = false;
                    "
                >
                    수정
                </CarbonButton>
            </template>
        </b-modal>

        <!-- 시설 추가/수정 모달 -->
        <b-modal
            v-model="showFormModal"
            :title="facilityFormMode === 'create' ? '시설 등록' : '시설 수정'"
            size="md"
            centered
        >
            <template #modal-header="{ close }">
                <div class="modal-form-header">
                    <h5 class="modal-title">
                        {{ facilityFormMode === "create" ? "시설 등록" : "시설 수정" }}
                    </h5>
                    <button type="button" class="close" @click="close">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
            </template>
            <form @submit.prevent="saveFacility" class="modal-form-content">
                <div class="form-section">
                    <div class="form-group">
                        <label>시설 코드 </label>
                        <input
                            v-model="facilityForm.facilityCode"
                            type="text"
                            class="form-control"
                            maxlength="50"
                            required
                            placeholder="예: FAC-001"
                            :readonly="facilityFormMode === 'edit'"
                        />
                        <small v-if="facilityFormMode === 'edit'" class="form-text text-muted"
                            >시설 코드는 수정할 수 없습니다.</small
                        >
                    </div>
                    <div class="form-group">
                        <label>시설명 </label>
                        <input
                            v-model="facilityForm.facilityName"
                            type="text"
                            class="form-control"
                            maxlength="100"
                            required
                            placeholder="예: 본사 사옥"
                        />
                    </div>
                    <div class="form-group">
                        <label>시설 유형</label>
                        <select v-model="facilityForm.facilityType" class="form-control">
                            <option value="">선택하세요</option>
                            <option v-for="opt in facilityTypeOptions" :key="opt.value" :value="opt.value">
                                {{ opt.text }}
                            </option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label>조직 </label>
                        <select v-model="facilityForm.orgIdx" class="form-control" required>
                            <option :value="null">선택하세요</option>
                            <option v-for="opt in organizationOptions" :key="opt.value" :value="opt.value">
                                {{ opt.text }}
                            </option>
                        </select>
                        <small v-if="organizationOptions.length === 0" class="form-text text-muted"
                            >등록된 조직이 없으면 먼저 조직을 등록하세요.</small
                        >
                    </div>

                    <div class="form-group">
                        <label>주소</label>
                        <input
                            v-model="facilityForm.address"
                            type="text"
                            class="form-control"
                            maxlength="500"
                            placeholder="주소"
                        />
                    </div>
                    <div class="row">
                        <div class="col-md-6">
                            <div class="form-group">
                                <label>국가</label>
                                <select v-model="facilityForm.country" class="form-control">
                                    <option value="">선택하세요</option>
                                    <option v-for="opt in regionOptions" :key="opt.value" :value="opt.value">
                                        {{ opt.text }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="form-group">
                                <label>지역</label>
                                <input
                                    v-model="facilityForm.region"
                                    type="text"
                                    class="form-control"
                                    maxlength="100"
                                    placeholder="예: 서울"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="form-group">
                        <label>법인</label>
                        <input
                            v-model="facilityForm.legalEntity"
                            type="text"
                            class="form-control"
                            maxlength="200"
                            placeholder="법인명"
                        />
                    </div>
                    <div v-if="facilityFormMode === 'edit'" class="form-group">
                        <label>상태</label>
                        <select v-model="facilityForm.status" class="form-control">
                            <option value="">선택하세요</option>
                            <option v-for="opt in statusOptions" :key="opt.value" :value="opt.value">
                                {{ opt.text }}
                            </option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label>비고</label>
                        <textarea
                            v-model="facilityForm.remarks"
                            class="form-control"
                            rows="2"
                            maxlength="1000"
                            placeholder="비고"
                        ></textarea>
                    </div>
                </div>
            </form>
            <template #modal-footer>
                <CarbonButton variant="secondary" :disabled="saving" @click="showFormModal = false">취소</CarbonButton>
                <CarbonButton variant="primary" :loading="saving" @click="saveFacility">저장</CarbonButton>
            </template>
        </b-modal>

        <!-- 시설 증빙 모달 -->
        <EvidenceModal
            :visible="showFacilityEvidenceModal"
            evidence-category="FACILITY"
            :entity-idx="facilityEvidenceTarget?.facilityIdx ?? null"
            :modal-title-override="
                facilityEvidenceTarget
                    ? `증빙자료 - ${facilityEvidenceTarget.facilityName || facilityEvidenceTarget.facilityCode || '시설'}`
                    : ''
            "
            @close="showFacilityEvidenceModal = false"
        />

        <FacilityAssigneesModal
            v-model="showAssigneesModal"
            :facility="assigneesFacilityTarget"
            @updated="loadFacilities"
        />
    </div>
</template>

<script>
import IconButton from "../../common/IconButton.vue";
import CarbonButton from "../../common/CarbonButton.vue";
import EvidenceModal from "../../activity/modals/EvidenceModal.vue";
import TablePagination from "../../common/TablePagination.vue";
import FacilityAssigneesModal from "../modals/FacilityAssigneesModal.vue";
import carbonApi from "../../../api";
import CarbonCodes from "../../../codes";

export default {
    name: "FacilitySettingsTab",
    components: {
        IconButton,
        CarbonButton,
        EvidenceModal,
        TablePagination,
        FacilityAssigneesModal,
    },
    props: {
        searchKeyword: { type: String, default: "" },
        filterStatus: { type: String, default: "" },
        filterOrg: { type: [Number, String], default: null },
        organizations: { type: Array, default: () => [] },
    },
    data() {
        return {
            facilities: [],
            loading: false,
            pagination: {
                page: 1,
                limit: 10,
                total: 0,
                totalPages: 1,
            },
            showDetailModal: false,
            selectedFacility: null,
            showFormModal: false,
            facilityFormMode: "create",
            facilityForm: this.getEmptyFacilityForm(),
            saving: false,
            showFacilityEvidenceModal: false,
            facilityEvidenceTarget: null,
            showAssigneesModal: false,
            assigneesFacilityTarget: null,
        };
    },
    computed: {
        filteredFacilities() {
            let list = this.facilities;
            const hasOrgFilter = this.filterOrg != null && this.filterOrg !== "" && Number(this.filterOrg) > 0;
            if (hasOrgFilter) {
                const orgId = Number(this.filterOrg);
                list = list.filter((f) => f.orgIdx == orgId);
            }
            if (!this.searchKeyword || !this.searchKeyword.trim()) {
                return list;
            }
            const kw = this.searchKeyword.trim().toLowerCase();
            return list.filter(
                (f) =>
                    (f.facilityCode && f.facilityCode.toLowerCase().includes(kw)) ||
                    (f.facilityName && f.facilityName.toLowerCase().includes(kw)) ||
                    (f.facilityType && f.facilityType.toLowerCase().includes(kw)) ||
                    (f.address && f.address.toLowerCase().includes(kw)),
            );
        },
        paginatedFacilities() {
            return this.filteredFacilities;
        },
        regionOptions() {
            return [
                { value: "KR", text: "한국" },
                { value: "EU", text: "유럽" },
            ];
        },
        facilityTypeOptions() {
            return CarbonCodes.facilityType.getList();
        },
        statusOptions() {
            return CarbonCodes.status.getList();
        },
        organizationOptions() {
            return this.organizations.map((org) => ({
                value: org.orgIdx,
                text: org.orgName
                    ? `${org.orgName} (${org.orgCode || org.orgIdx})`
                    : org.orgCode || `조직 #${org.orgIdx}`,
            }));
        },
    },
    mounted() {
        this.loadFacilities();
    },
    methods: {
        getEmptyFacilityForm() {
            return {
                facilityCode: "",
                facilityName: "",
                orgIdx: null,
                facilityType: "",
                address: "",
                latitude: null,
                longitude: null,
                country: "",
                region: "",
                legalEntity: "",
                status: "ACTIVE",
                remarks: "",
            };
        },
        getOrgLabel(orgIdx) {
            if (orgIdx == null) return "-";
            const org = this.organizations.find((o) => o.orgIdx === orgIdx);
            return org ? org.orgName || org.orgCode || `#${orgIdx}` : `#${orgIdx}`;
        },
        formatStatus(status) {
            return CarbonCodes.status.getLabel(status);
        },
        formatFacilityType(value) {
            return CarbonCodes.facilityType.getLabel(value);
        },
        onFacilityPageChange(page) {
            this.pagination.page = Math.max(1, Math.min(page, this.pagination.totalPages || 1));
            this.loadFacilities();
        },
        onFacilityPageSizeChange(size) {
            this.pagination.limit = size;
            this.pagination.page = 1;
            this.loadFacilities();
        },
        async loadFacilities(statusOverride) {
            this.loading = true;
            try {
                const params = {
                    page: this.pagination.page,
                    limit: this.pagination.limit,
                };
                const status = statusOverride !== undefined ? statusOverride : this.filterStatus;
                if (status) params.status = status;
                const res = await carbonApi.utility.getFacilities(null, params);
                const data = res.data?.data != null ? res.data.data : res.data;
                this.facilities = (data && data.facilities) || [];
                if (data && data.pagination) {
                    this.pagination = {
                        page: data.pagination.page,
                        limit: data.pagination.limit,
                        total: data.pagination.total,
                        totalPages:
                            data.pagination.totalPages ||
                            Math.ceil((data.pagination.total || 0) / (data.pagination.limit || 10)),
                    };
                }
            } catch (err) {
                console.error("[FacilitySettingsTab] 시설 목록 조회 실패:", err);
                this.$bvToast?.toast(err.response?.data?.message || "시설 목록을 불러오지 못했습니다.", {
                    title: "오류",
                    variant: "danger",
                    autoHideDelay: 5000,
                });
                this.facilities = [];
            } finally {
                this.loading = false;
            }
        },
        viewFacilityDetail(facility) {
            this.selectedFacility = facility;
            this.showDetailModal = true;
        },
        openAssigneesModal(facility) {
            if (!facility?.facilityIdx) {
                this.$bvToast?.toast("시설 정보를 불러올 수 없습니다.", { variant: "warning" });
                return;
            }
            this.assigneesFacilityTarget = {
                facilityIdx: facility.facilityIdx,
                facilityName: facility.facilityName,
                facilityCode: facility.facilityCode,
            };
            this.showAssigneesModal = true;
        },
        openFacilityEvidenceModal(facility) {
            if (!facility?.facilityIdx) {
                this.$bvToast?.toast("시설 정보를 불러올 수 없습니다.", { variant: "warning" });
                return;
            }
            this.facilityEvidenceTarget = {
                facilityIdx: facility.facilityIdx,
                facilityName: facility.facilityName,
                facilityCode: facility.facilityCode,
            };
            this.showFacilityEvidenceModal = true;
        },
        openFacilityModal(mode, facility = null) {
            this.facilityFormMode = mode;
            if (mode === "create") {
                this.facilityForm = { ...this.getEmptyFacilityForm() };
            } else if (facility) {
                this.facilityForm = {
                    facilityCode: facility.facilityCode,
                    facilityName: facility.facilityName || "",
                    orgIdx: facility.orgIdx != null ? facility.orgIdx : null,
                    facilityType: facility.facilityType || "",
                    address: facility.address || "",
                    latitude: facility.latitude ?? null,
                    longitude: facility.longitude ?? null,
                    country: facility.country || "",
                    region: facility.region || "",
                    legalEntity: facility.legalEntity || "",
                    status: facility.status || "active",
                    remarks: facility.remarks || "",
                };
            }
            this.showFormModal = true;
        },
        async saveFacility() {
            const form = this.facilityForm;
            if (!form.facilityCode || !form.facilityName) {
                this.$bvToast?.toast("시설 코드와 시설명을 입력하세요.", { variant: "warning" });
                return;
            }
            const orgIdx = form.orgIdx != null && form.orgIdx !== "" ? Number(form.orgIdx) : null;
            if (orgIdx == null || orgIdx <= 0) {
                this.$bvToast?.toast("조직을 선택하세요.", { variant: "warning" });
                return;
            }
            this.saving = true;
            try {
                const payload = {
                    facilityCode: form.facilityCode.trim(),
                    facilityName: form.facilityName.trim(),
                    orgIdx,
                    facilityType: form.facilityType?.trim() || null,
                    address: form.address?.trim() || null,
                    latitude: form.latitude != null && form.latitude !== "" ? Number(form.latitude) : null,
                    longitude: form.longitude != null && form.longitude !== "" ? Number(form.longitude) : null,
                    country: form.country?.trim() || null,
                    region: form.region?.trim() || null,
                    legalEntity: form.legalEntity?.trim() || null,
                    remarks: form.remarks?.trim() || null,
                };
                if (this.facilityFormMode === "create") {
                    await carbonApi.utility.createFacility(payload);
                    this.$bvToast?.toast("시설이 등록되었습니다.", { variant: "success" });
                } else {
                    const updatePayload = { ...payload };
                    delete updatePayload.facilityCode;
                    updatePayload.status = form.status || "active";
                    updatePayload.orgIdx = orgIdx;
                    await carbonApi.utility.updateFacility(form.facilityCode, updatePayload);
                    this.$bvToast?.toast("시설이 수정되었습니다.", { variant: "success" });
                }
                this.showFormModal = false;
                await this.loadFacilities();
            } catch (err) {
                const msg = err.response?.data?.message || err.message || "저장에 실패했습니다.";
                this.$bvToast?.toast(msg, { title: "오류", variant: "danger", autoHideDelay: 5000 });
            } finally {
                this.saving = false;
            }
        },
        async confirmDeleteFacility(facility) {
            if (!facility || !facility.facilityCode) return;

            const code = facility.facilityCode;
            const name = facility.facilityName || "";
            const confirm = await this.alertConfirmWarning(
                name
                    ? `시설 '${code} / ${name}'을(를) 삭제하시겠습니까?\n연결된 데이터가 있으면 삭제할 수 없습니다.`
                    : `시설 '${code}'을(를) 삭제하시겠습니까?\n연결된 데이터가 있으면 삭제할 수 없습니다.`,
            );
            if (!confirm.value) return;

            try {
                await carbonApi.utility.deleteFacility(code);
                this.$bvToast?.toast("시설이 삭제되었습니다.", { variant: "success" });
                await this.loadFacilities();
            } catch (err) {
                const msg =
                    err.response?.data?.message ||
                    (err.response?.status === 409
                        ? "시설에 연결된 데이터가 있어 삭제할 수 없습니다."
                        : err.message || "시설 삭제에 실패했습니다.");
                this.$bvToast?.toast(msg, { title: "오류", variant: "danger", autoHideDelay: 5000 });
            }
        },
    },
};
</script>

<style scoped>
.facility-settings-tab {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

.modal-detail-header,
.modal-form-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
}

.form-section {
    margin-bottom: 16px;
}

.section-header {
    margin-bottom: 8px;
}

.section-header h6 {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    color: #475569;
}

.form-group {
    margin-bottom: 12px;
}

.form-group label {
    display: block;
    margin-bottom: 4px;
    font-size: 12px;
    font-weight: 500;
}

.form-group .form-control {
    width: 100%;
    padding: 6px 10px;
    font-size: 13px;
    border: 1px solid #ced4da;
    border-radius: 4px;
}

.required {
    color: #dc3545;
}
</style>
